// Sprint 6 UI atoms — evidence-aware overlays for the AI Queue card.
// Kept as small named components so the parent page stays readable.

import { useState } from "react";
import {
  Sparkles, Layers, AlertTriangle, CheckCircle2, XCircle, Info,
  ChevronDown, ChevronUp, Quote, ExternalLink, ShieldCheck, Calculator,
  Eye, HelpCircle,
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { EVIDENCE_CLASSES } from "@/components/EvidenceClass";

/* ─── Signal relationship ───────────────────────────────────────── */

const SIGNAL_TONE = {
  new: "bg-intel-50 text-intel-800 border-intel-200",
  corroborating: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplicate: "bg-amber-50 text-amber-700 border-amber-200",
};
const SIGNAL_LABEL = {
  new: "إشارة جديدة",
  corroborating: "إشارة مؤكِّدة",
  duplicate: "مكرَّرة",
};

export const SignalBadge = ({ newsId, correlation }) => {
  if (!correlation) return null;
  return (
    <span
      data-testid={ADMIN.aiSignalBadge(newsId)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${SIGNAL_TONE[correlation.relationship] || SIGNAL_TONE.new}`}
      title={correlation.reason || ""}
    >
      <Layers className="h-3 w-3" strokeWidth={1.75} />
      {SIGNAL_LABEL[correlation.relationship] || correlation.relationship}
      <span className="font-mono text-[10px] opacity-70">
        · {(correlation.similarity_score * 100).toFixed(0)}%
      </span>
    </span>
  );
};

/* ─── OMI ───────────────────────────────────────────────────────── */

const OMI_TONE = {
  1: "bg-surface-100 text-ink-700 border-surface-200",
  2: "bg-intel-50 text-intel-800 border-intel-200",
  3: "bg-emerald-50 text-emerald-700 border-emerald-200",
  4: "bg-emerald-100 text-emerald-800 border-emerald-300",
  5: "bg-emerald-200 text-emerald-900 border-emerald-400",
};

export const OMIBadge = ({ newsId, omi }) => {
  if (!omi) return null;
  return (
    <span
      data-testid={ADMIN.aiOmiBadge(newsId)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${OMI_TONE[omi.level]}`}
      title={omi.rationale || ""}
    >
      <ShieldCheck className="h-3 w-3" strokeWidth={1.75} />
      OMI-{omi.level} · {omi.label}
    </span>
  );
};

/* ─── Evidence composition ──────────────────────────────────────── */

const EV_TILE = ({ label, count, tone }) => (
  <div className={`flex-1 rounded-md border px-3 py-2 text-center ${tone}`}>
    <div className="font-mono text-lg font-semibold">{count}</div>
    <div className="text-[10px] font-medium">{label}</div>
  </div>
);

export const EvidenceComposition = ({ newsId, composition }) => {
  if (!composition) return null;
  const total = ["verified", "estimated", "expert_assessment", "unknown"]
    .reduce((n, k) => n + (composition[k] || 0), 0);

  return (
    <div
      data-testid={ADMIN.aiEvidenceComp(newsId)}
      className="mt-4 rounded-md border border-surface-200 bg-white p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-widest text-intel-800">
          تركيبة الأدلّة
        </div>
        {total > 0 && (
          <span dir="ltr" className="num font-mono text-[10px] text-ink-500">
            {total} claim{total !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {["verified", "estimated", "expert_assessment", "unknown"].map((k) => {
          const c = EVIDENCE_CLASSES[k];
          return (
            <EV_TILE
              key={k}
              label={c.label}
              count={composition[k] || 0}
              tone={c.tone}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ─── Conflicts ─────────────────────────────────────────────────── */

export const ConflictsSection = ({ newsId, conflicts }) => {
  if (!conflicts || !conflicts.has_conflicts) return null;
  return (
    <div
      data-testid={ADMIN.aiConflictsSection(newsId)}
      className="mt-4 rounded-md border border-red-200 bg-red-50/60 p-4"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
        <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
        تعارضات بين المصادر ({conflicts.conflicts.length})
      </div>
      <ul className="mt-3 space-y-3">
        {conflicts.conflicts.map((c, i) => (
          <li key={i} className="rounded-md border border-red-200 bg-white p-3 text-sm">
            <div className="font-mono text-[11px] text-red-700">{c.field}</div>
            <p className="mt-1 text-xs text-ink-700">{c.explanation}</p>
            <ul className="mt-2 space-y-1">
              {c.values.map((v, j) => (
                <li key={j} className="flex items-start gap-2 text-xs">
                  <span className="rounded bg-red-50 px-1.5 py-0.5 font-mono text-[10px] text-red-700">
                    {typeof v.value === "object" ? JSON.stringify(v.value) : String(v.value)}
                  </span>
                  <span className="text-ink-500">— {v.source_name}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ─── Field evidence detail (expandable) ────────────────────────── */

const CLASS_STYLE = {
  verified:          { Icon: EVIDENCE_CLASSES.verified.icon,          tone: EVIDENCE_CLASSES.verified.tone,          label: EVIDENCE_CLASSES.verified.label },
  estimated:         { Icon: EVIDENCE_CLASSES.estimated.icon,         tone: EVIDENCE_CLASSES.estimated.tone,         label: EVIDENCE_CLASSES.estimated.label },
  expert_assessment: { Icon: EVIDENCE_CLASSES.expert_assessment.icon, tone: EVIDENCE_CLASSES.expert_assessment.tone, label: EVIDENCE_CLASSES.expert_assessment.label },
  unknown:           { Icon: EVIDENCE_CLASSES.unknown.icon,           tone: EVIDENCE_CLASSES.unknown.tone,           label: EVIDENCE_CLASSES.unknown.label },
};

const FIELD_LABEL = {
  estimated_capital: "رأس المال التقديري",
  market_size: "حجم السوق",
  growth_rate: "معدّل النموّ",
  deadline: "الموعد النهائي",
  roi_or_returns: "العائد المتوقّع",
  regulatory_status: "الوضع التنظيمي",
  beneficiaries_count: "عدد المستفيدين",
  project_value: "قيمة المشروع",
};

const renderValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") {
    if (v.min !== undefined || v.max !== undefined) {
      const currency = v.currency ? ` ${v.currency}` : "";
      if (v.min === v.max) return `${v.min}${currency}`;
      return `${v.min}–${v.max}${currency}`;
    }
    return JSON.stringify(v);
  }
  return String(v);
};

export const FieldEvidence = ({ newsId, fieldEvidence }) => {
  const [expanded, setExpanded] = useState(false);
  if (!fieldEvidence || !Object.keys(fieldEvidence).length) return null;
  const entries = Object.entries(fieldEvidence);

  return (
    <div
      data-testid={ADMIN.aiFieldEvidence(newsId)}
      className="mt-4 rounded-md border border-surface-200 bg-white"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-4 py-3 text-right"
      >
        <span className="text-[11px] font-medium uppercase tracking-widest text-intel-800">
          أدلّة الحقول المادّية ({entries.length})
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-ink-500" strokeWidth={1.75} />
                  : <ChevronDown className="h-4 w-4 text-ink-500" strokeWidth={1.75} />}
      </button>
      {expanded && (
        <ul className="divide-y divide-surface-100">
          {entries.map(([field, ev]) => {
            const style = CLASS_STYLE[ev.evidence_class] || CLASS_STYLE.unknown;
            const Icon = style.Icon;
            return (
              <li key={field} className="px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-ink-900">
                    {FIELD_LABEL[field] || field}
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${style.tone}`}>
                    <Icon className="h-3 w-3" strokeWidth={1.75} />
                    {style.label}
                    <span className="font-mono opacity-70">· {ev.confidence}%</span>
                  </span>
                </div>
                <div className="mt-1.5 text-sm text-ink-700">
                  <span className="text-ink-500">القيمة: </span>
                  <span className="font-mono">{renderValue(ev.value)}</span>
                </div>
                {ev.methodology && (
                  <p className="mt-1 text-xs text-ink-500">
                    <span className="font-medium text-ink-700">المنهجية: </span>{ev.methodology}
                  </p>
                )}
                {ev.assumptions?.length > 0 && (
                  <div className="mt-1 text-xs text-ink-500">
                    <span className="font-medium text-ink-700">الافتراضات: </span>
                    <ul className="mt-0.5 space-y-0.5">
                      {ev.assumptions.map((a, i) => <li key={i}>· {a}</li>)}
                    </ul>
                  </div>
                )}
                {ev.unknown_reason && (
                  <p className="mt-1 text-xs italic text-ink-500">
                    <span className="font-medium text-ink-700">سبب عدم المعرفة: </span>{ev.unknown_reason}
                  </p>
                )}
                {ev.sources?.length > 0 && (
                  <p className="mt-1 text-xs text-ink-500">
                    <span className="font-medium text-ink-700">المصادر: </span>{ev.sources.join(" · ")}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* ─── Decision log (expandable) ─────────────────────────────────── */

export const DecisionLog = ({ newsId, entries }) => {
  const [expanded, setExpanded] = useState(false);
  if (!entries || entries.length === 0) return null;
  return (
    <div
      data-testid={ADMIN.aiDecisionLog(newsId)}
      className="mt-4 rounded-md border border-surface-200 bg-white"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-4 py-3 text-right"
      >
        <span className="text-[11px] font-medium uppercase tracking-widest text-intel-800">
          سجلّ القرارات ({entries.length})
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-ink-500" strokeWidth={1.75} />
                  : <ChevronDown className="h-4 w-4 text-ink-500" strokeWidth={1.75} />}
      </button>
      {expanded && (
        <ol className="divide-y divide-surface-100 text-xs">
          {entries.map((e, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr] gap-3 px-4 py-2.5">
              <span className="font-mono text-[10px] text-ink-500">
                {e.timestamp.split("T")[1]?.slice(0, 8) || "—"}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-intel-50 px-1.5 py-0.5 text-[10px] font-medium text-intel-800">
                    {e.stage}
                  </span>
                  <span className="text-ink-500">·</span>
                  <span className="font-medium text-ink-900">{e.actor}</span>
                  <span className="text-ink-500">·</span>
                  <span className="text-ink-700">{e.action}</span>
                  {e.agent_version && (
                    <span className="text-[10px] font-mono text-ink-500">({e.agent_version})</span>
                  )}
                </div>
                {e.reason && <p className="mt-0.5 text-ink-500">{e.reason}</p>}
                {(e.previous_status || e.new_status) && (
                  <p className="mt-0.5 text-[10px] text-ink-500">
                    {e.previous_status ? `من ${e.previous_status}` : ""}
                    {e.previous_status && e.new_status ? " → " : ""}
                    {e.new_status ? `إلى ${e.new_status}` : ""}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

/* ─── Sources list ──────────────────────────────────────────────── */

export const SourcesList = ({ sources }) => {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-4 rounded-md border border-surface-200 bg-white p-3">
      <div className="text-[11px] font-medium uppercase tracking-widest text-intel-800">
        المصادر ({sources.length})
      </div>
      <ul className="mt-2 space-y-1.5">
        {sources.map((s, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-ink-700">
            <Quote className="h-3 w-3 shrink-0 text-ink-500" strokeWidth={1.75} />
            <span className="font-medium">{s.name}</span>
            {s.url && (
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-0.5 text-intel-800 hover:underline"
              >
                <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
